/**
 * Domain: Velocity mapping.
 *
 * Map a "height range" (max_height - min_height) into a MIDI velocity range.
 * Pure logic only; no I/O.
 */

/**
 * Settings for mapping height range delta to MIDI velocity.
 */
export interface VelocitySettings {
  minDelta: number;
  maxDelta: number;
  minVelocity: number;
  maxVelocity: number;
}

/**
 * Clamp to inclusive [min, max].
 */
function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/**
 * Map a heightRangeDelta (max_height - min_height) into a MIDI velocity value.
 *
 * @param heightRangeDelta - The difference between max and min height
 * @param settings - Mapping configuration
 * @returns Integer MIDI velocity (0..127)
 */
export function mapHeightRangeToVelocity(
  heightRangeDelta: number,
  settings: VelocitySettings
): number {
  if (!settings) throw new Error('settings is required');
  const { minDelta, maxDelta, minVelocity, maxVelocity } = settings;

  if (!Number.isFinite(minDelta) || !Number.isFinite(maxDelta) || maxDelta <= minDelta) {
    throw new Error('minDelta/maxDelta must be finite and maxDelta must be > minDelta');
  }
  if (!Number.isFinite(minVelocity) || !Number.isFinite(maxVelocity) || maxVelocity < minVelocity) {
    throw new Error('minVelocity/maxVelocity must be finite and maxVelocity must be >= minVelocity');
  }

  const v = Number(heightRangeDelta);
  if (!Number.isFinite(v)) return clamp(Math.round(minVelocity), 0, 127);

  const clamped = clamp(v, minDelta, maxDelta);
  const normalized = (clamped - minDelta) / (maxDelta - minDelta);
  const velocity = minVelocity + normalized * (maxVelocity - minVelocity);
  return clamp(Math.round(velocity), 0, 127);
}

